import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listConversations,
  getConversation,
  sendMessage,
  getSuggestedReply,
  regenerateSuggestedReply,
  type ConversationFilter,
} from './api'

export function useConversations(params?: {
  limit?: number
  cursor?: string
  type?: 'SMS' | 'VOICE'
  filter?: ConversationFilter
}) {
  return useQuery({
    queryKey: ['conversations', params],
    queryFn: () => listConversations(params),
  })
}

export function useConversation(id: string, params?: { limit?: number; cursor?: string }) {
  return useQuery({
    queryKey: ['conversation', id, params],
    queryFn: () => getConversation(id, params),
    enabled: !!id,
    refetchInterval: 5000,
  })
}

/**
 * A voice call's transcript, fetched lazily when its inline card is expanded.
 * Transcripts are immutable once written (the worker creates the whole VOICE
 * conversation in one insert), so no poll — and a distinct query key keeps it
 * from colliding with the 5s-polling thread query above.
 */
export function useCallTranscript(id: string, expanded: boolean) {
  return useQuery({
    queryKey: ['conversation', id, 'transcript'],
    queryFn: () => getConversation(id, { limit: 200 }),
    enabled: !!id && expanded,
    staleTime: Infinity,
  })
}

/**
 * Send a reply in a conversation. On success we invalidate that conversation's
 * detail query so the new (outbound, "processing") message appears immediately;
 * the 5s poll on useConversation then reflects delivery status as the worker
 * relays it.
 */
export function useSendMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      body,
      needsYou,
      handoffRequestId,
    }: {
      id: string
      body: string
      needsYou?: Parameters<typeof sendMessage>[2]
      handoffRequestId?: string
    }) => sendMessage(id, body, needsYou, handoffRequestId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

/**
 * The durable AI-drafted starting point for a reply composer (#538). The read
 * is a plain DB fetch — generation already happened in the worker when the
 * inbound arrived — so polling is safe and free: while the server reports
 * `pending`, refetch on a short interval until the draft lands. The draft is
 * a seed the owner edits; consumers seed the composer once, so a refetch can
 * never fight their typing. `suggestion: null` with `pending: false` means
 * the model had nothing usable — the composer simply starts blank.
 */
/**
 * Ask for a fresh draft. Mints the next immutable revision server-side; the
 * read query then polls (`pending`) until it lands, so there is nothing to
 * optimistically write here — invalidating is enough.
 */
export function useRegenerateSuggestedReply(id: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => regenerateSuggestedReply(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id, 'suggested-reply'] })
    },
  })
}

export function useSuggestedReply(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['conversation', id, 'suggested-reply'],
    queryFn: () => getSuggestedReply(id!),
    enabled: !!id && enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.pending ? 4_000 : false),
  })
}
